import { actions } from '@ecosystem/newron-design-system';
import React, { useEffect, useState } from 'react';

import { ContextManager } from '../context/manager';
import { useMobile } from '../context/mobile/mobile.provider';
import { mapDefinitions } from '../export/utils/mapDefinitions';
import { Context, FilterDefinition, FilterSections, FilterType, FiltersValues } from '../types';

import { SaveFilterAction } from './saveFilter.button';
import { SectionWithDefinitions } from './types';
import { adjustDateRangeFilter, adjustNumericFilter, getJoinedLabel } from './utils';

const { Chips } = actions;

export const getValuesDefinitions = (sections: FilterSections[], values: FiltersValues): SectionWithDefinitions[] => {
  return sections
    .map(section => {
      const sectionDefinitions = mapDefinitions([section]).filter(def => !!values[def?.name]);
      return [section, sectionDefinitions] as SectionWithDefinitions;
    })
    .filter(([_section, definitions]) => definitions.length > 0);
};

export const getChipValue = (def: FilterDefinition, values: FiltersValues) => {
  const value = getJoinedLabel(values[def.name]);
  return def.type === FilterType.numericRange ? adjustNumericFilter(def.type, value) : value;
};

function FilterChips() {
  const [chipsState, setChipsState] = useState<{
    values: FiltersValues;
    sectionsWithDefinitions: SectionWithDefinitions[];
  }>();

  const { isMobile } = useMobile();

  const contextSubscriber = (context: Context) => {
    if (!context?.filters?.sections?.length) {
      return;
    }

    const { sections, values } = context.filters;
    const sectionsWithDefinitions = getValuesDefinitions(sections, values);
    setChipsState({ values, sectionsWithDefinitions });
  };

  useEffect(() => {
    ContextManager.getInstance().subscribeToSettingsChanges(contextSubscriber);
    return () => ContextManager.getInstance().unsubscribeFromSettingsChanges(contextSubscriber);
  }, []);

  const handleChipClick = (def: FilterDefinition) => {
    ContextManager.getInstance().updateFilterValues({
      [def.name]: [],
    });
  };

  const handleClearAll = () => {
    ContextManager.getInstance().clearFilterValues();
  };

  if (!chipsState || !Object.keys(chipsState.values).length) {
    return null;
  }

  const { values, sectionsWithDefinitions } = chipsState;

  if (!sectionsWithDefinitions.length) {
    return null;
  }

  const ChipsAction = (
    <>
      <Chips.Actions.ClearAll onClick={handleClearAll} />
      <SaveFilterAction sectionsWithDefinitions={sectionsWithDefinitions} values={values} />
    </>
  );

  return (
    <div className="bg-white rounded mb-4 px-3 py-2">
      <Chips>
        <Chips.Label>Filters:</Chips.Label>
        <Chips.List>
          {sectionsWithDefinitions
            .flatMap(([_section, definitions]) => definitions)
            .map(def => (
              <Chips.Chip
                key={def.name}
                value={getChipValue(def, values)}
                label={def.label}
                onRemove={() => handleChipClick(def)}
              />
            ))}
        </Chips.List>
        {!isMobile && <Chips.Actions>{ChipsAction}</Chips.Actions>}
      </Chips>
      {isMobile && <div className="flex justify-center">{ChipsAction}</div>}
    </div>
  );
}

export { FilterChips, adjustDateRangeFilter };
